import { createContext, useContext } from 'react';
import type { ComponentProps, ElementType, ReactNode } from 'react';

import type { ExtraProps } from 'react-markdown';

import { slugify } from './slugify';
import type { MarkdownHeading } from './useMarkdownHeadings';

const InsideBlockquoteContext = createContext(false);

// Plain, immutable data — RecapH3 looks its own id up by source line number
// rather than consuming from a shared position counter. A counter mutated
// during render doesn't survive React StrictMode's double-invocation of
// render bodies (dev only): the mutation from the first, discarded pass
// isn't undone before the second, causing every id after the first heading
// to shift by one. A pure lookup by line number has no such state to drift.
export const RecapHeadingsContext = createContext<MarkdownHeading[] | null>(null);

const getPlainText = (node: ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getPlainText).join('');
  return '';
};

const blockquoteClassName = 'my-3 border-l-4 border-gx-highlight bg-gx-tile-dark p-4';

export const RecapBlockquote: ElementType<ComponentProps<'blockquote'> & ExtraProps> = ({
  node: _node,
  children,
  className,
  ...props
}) => (
  <blockquote
    {...props}
    className={[blockquoteClassName, className].filter(Boolean).join(' ')}
  >
    <InsideBlockquoteContext value>{children}</InsideBlockquoteContext>
  </blockquote>
);

const topLevelHeadingClassName =
  'mt-6 mb-3 border-b border-border pb-2 text-base font-bold text-foreground';
const calloutHeadingClassName = 'mb-2 text-sm font-bold text-gx-highlight';

export const RecapH3: ElementType<ComponentProps<'h3'> & ExtraProps> = ({
  node,
  children,
  className,
  ...props
}) => {
  const insideBlockquote = useContext(InsideBlockquoteContext);
  const headings = useContext(RecapHeadingsContext);

  const line = node?.position?.start.line;
  const matchedHeading = line !== undefined && headings?.find(heading => heading.line === line);
  const id = matchedHeading ? matchedHeading.id : slugify(getPlainText(children));
  const headingClassName = insideBlockquote ? calloutHeadingClassName : topLevelHeadingClassName;

  return (
    <h3
      {...props}
      className={[headingClassName, className].filter(Boolean).join(' ')}
      id={id}
    >
      {children}
    </h3>
  );
};
